// The shell: one page for the intro, the lab and (later) the projects. It owns the router, the one audio
// element, the one render loop and the shared stage. Views come and go inside #root.
import "./base.css";
import "./radio/radio.css";
import introUrl from "../../../assets/intro.mp3?url";
import { ROUTES, normalise, partOf } from "./routes";
import { watchDeploys } from "./deploy";
import type { ShellContext, Stage, View } from "./types";
import notFound from "./views/not-found";
import { createProgress, FORMAT } from "@youbuild/progress";
import { createRadio } from "./radio/radio";
import { mountPlayer } from "./radio/player";
import { createAudioScene } from "./audio/scene";
import { quality, perfOverlay } from "./quality";
import { frames } from "./frames";
import { forgetSiteReturn } from "./project/back.js";

const canvas = document.querySelector<HTMLCanvasElement>("#c")!;
const root = document.querySelector<HTMLElement>("#root")!;
const audio = new Audio(introUrl); audio.preload = "auto";
const progress = createProgress();
const deploys = watchDeploys({ progress, formatNow: FORMAT });
const sound = { off: () => progress.pref("sound") === "off", remember: (on: boolean) => progress.setPref("sound", on ? "on" : "off") };
// the radio belongs to the shell, so it keeps playing (and keeps its place on screen) while views come and go
const radio = createRadio(progress, sound); mountPlayer(radio);
// the way into the lab, as sound: the intro song outside, the radio inside (src/audio/scene.ts)
const scene = createAudioScene(audio, radio, sound);

let stageP: Promise<Stage> | undefined;
// the city canvas shows only while a view uses the stage (the intro and the lab; a project has its own 3D)
const stage = () => { canvas.hidden = false; return (stageP ??= import("./scene/stage.js").then(m => m.createStage(canvas))); };

/* ---- the mounted view and what the shell cleans up after it ---- */
type Mounted = { view: View; path: string; ac: AbortController; styles: HTMLStyleElement[]; frame?: (now: number) => void; resize?: () => void; held?: boolean };
let current: Mounted | undefined;
let seq = 0;

function unmount() {
  if (!current) return;
  const m = current; current = undefined;
  m.ac.abort(); m.view.unmount(); m.styles.forEach(s => s.remove()); root.replaceChildren();
}

async function go(to: string, push: boolean) {
  // the address's query goes with it (a team's project, ?team=, #2564); the views are routed by path
  const q = to.indexOf("?"), search = q < 0 ? "" : to.slice(q);
  let path = normalise(q < 0 ? to : to.slice(0, q));
  const my = ++seq;
  const load = ROUTES[path];
  // a newer deploy changed this part (or the shell): load the page fresh at a safe point only, never mid-journey or over the music
  const playing = !audio.paused || radio.state.playing;
  if (current && load && deploys.needsFreshLoad(partOf(path), partOf(current.path), playing)) { if (push) location.assign(path + search); else location.reload(); return; }
  // the view being left draws no more while the next one loads (#1749): on a phone its 3D's frames held the page, and the next view's
  // code waited seconds behind them after the tap; it draws again if this navigation fails
  const leaving = current; if (leaving && load) leaving.held = true;
  let view: View;
  try { view = load ? (await load()).default() : notFound(); }
  catch (e) { if (reloadOnce(path)) return; if (leaving && current === leaving && my === seq) leaving.held = false; throw e; }
  if (my !== seq) return;                                   // a newer navigation won
  // a project opened from the lab or the map goes back there, not to a site an earlier visit came from (#431, project/back.js)
  if (current && path.startsWith("/projects/") && !current.path.startsWith("/sites/")) forgetSiteReturn();
  if (push) history.pushState({}, "", path + search); else if (location.pathname !== path && load) history.replaceState({}, "", path + search);
  unmount();
  quality.placeChanged();                                  // a new place: the scene tier may try "full" again (src/quality.ts)
  canvas.hidden = true;
  const m: Mounted = { view, path, ac: new AbortController(), styles: [] };
  // a project opened for a team saves to the team's room, never to this browser; if the room does not answer, the page says so
  // instead of opening the learner's own save (views/classroom/teamshell.js)
  const room = path.startsWith("/projects/") ? new URLSearchParams(search).get("team") : null;
  const team = room ? await (await import("./views/classroom/teamshell.js")).openTeam({ room, projectId: partOf(path), base: progress, signal: m.ac.signal, root }) : null;
  if (my !== seq) { m.ac.abort(); return; }                 // a newer navigation won while the room answered
  if (room && !team) { m.view = { title: view.title, async mount() {}, unmount() {} }; current = m; document.title = view.title; return; }
  const ctx: ShellContext = {
    audio, sound, light, radio, scene, progress: team?.progress ?? progress, ...(team ? { team: team.team } : {}), signal: m.ac.signal, stage,
    useStyle(css) { const s = document.createElement("style"); s.textContent = css; document.head.appendChild(s); m.styles.push(s); },
    onFrame(fn) { m.frame = fn; },
    onResize(fn) { m.resize = fn; },
    navigate: p => { void go(p, true); },
    reopen: () => { if (current === m) void go(m.path + search, false); },   // (go without push keeps the history as it is)
    prefetch: p => { void prefetch(p); }
  };
  current = m;
  labAhead(m);
  document.title = view.title;
  document.body.dataset.route = path === "/film/" ? "intro" : path === "/" ? "front" : path === "/lab/" ? "lab" : path.startsWith("/projects/") ? "project" : "other";
  await view.mount(root, ctx);
  if (current !== m) return;
  m.resize?.();
  projectsAhead(m);
  try { sessionStorage.removeItem(RELOADED); } catch { /* ignore */ }
}

/* ---- every place's way out is the lab: as soon as a place's own code is in, the lab's is fetched (and run) too, so "Back to the
   lab" answers at once, even when a place draws it early in its mount (BS-001). Loading it on the click took 3 to 4 s on a busy
   computer, with nothing changing (#566). The front page and the film fetch it themselves (views/home), and Save-Data waits for the click. ---- */
let labFetched: Promise<boolean> | undefined;   // (for the tests: true once the lab's code is in)
function labAhead(m: Mounted) {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  if (m.path === "/" || m.path === "/film/" || m.path === "/lab/" || m.path === "/city/" || nav.connection?.saveData) return;
  labFetched ??= ROUTES["/lab/"]!().then(() => true, () => { labFetched = undefined; return false; });   // (failed: the click loads it again, or reloadOnce does)
}

/* ---- a view's code fetched ahead: its module, then the rest its preload() names (#1347). A failure is quiet: opening it later loads
   it then, or reloadOnce does. ---- */
const prefetched = new Map<string, Promise<unknown>>(), prefetchDone = new Set<string>();   // (started; finished, for the tests)
function prefetch(p: string): Promise<unknown> {
  const path = normalise(p), l = ROUTES[path]; if (!l) return Promise.resolve();
  if (!prefetched.has(path)) prefetched.set(path, l().then(m => m.preload?.()).then(() => { prefetchDone.add(path); }, () => { prefetched.delete(path); }));
  return prefetched.get(path)!;
}
/* ---- the projects are a click away from the lab: once the lab is in and the browser is idle, their code is fetched (each project's
   view and the code its mount needs), so opening one waits for no network (#1347). Not on Save-Data. ---- */
function projectsAhead(m: Mounted) {
  const nav = navigator as Navigator & { connection?: { saveData?: boolean } };
  if (m.path !== "/lab/" || nav.connection?.saveData) return;
  const idle = (f: () => void) => "requestIdleCallback" in window ? requestIdleCallback(f, { timeout: 4000 }) : setTimeout(f, 2000);
  idle(() => { if (current !== m) return; for (const p of Object.keys(ROUTES)) if (p.startsWith("/projects/")) void prefetch(p); });
}

/* ---- safety net: a view's code could not be loaded (a deploy removed it, or the network dropped).
   Load the page fresh once; saved work is already stored, because views save as the learner works. ---- */
const RELOADED = "yb-reloaded";
function reloadOnce(path: string): boolean {
  try { if (sessionStorage.getItem(RELOADED) === path) return false; sessionStorage.setItem(RELOADED, path); } catch { return false; }
  location.assign(path); return true;
}

/* ---- the one loop ---- */
// light mode (#2569): the learner's setting, remembered in this browser; an address with ?light (a Classroom link) turns it on for
// that visit, ?light=0 off
const lightAsked = new URLSearchParams(location.search).get("light");
const light = {
  on: () => quality.light,
  set(on: boolean, remember = true) { quality.setLight(on); if (remember) progress.setPref("light", on ? "on" : "off"); }
};
quality.onLight(on => frames.light(on));   // (on by the setting, the address, or by itself when frames stay slow)
light.set(lightAsked !== null ? lightAsked !== "0" : progress.pref("light") === "on", false);
// a held frame draws nothing: not counted. Nor is a frame the cap leaves out (src/frames.ts: 60 a second while the learner moves,
// 30 while nothing is touched; the intro, cut to the music, keeps every frame)
for (const t of ["pointerdown", "pointermove", "wheel", "keydown", "touchstart", "touchmove"]) addEventListener(t, () => { const now = performance.now(); frames.input(now); quality.input(now); }, { capture: true, passive: true });
// the view's own drawing is timed: on a 60 Hz screen frames never come faster than its rate, so only how long a frame takes to
// draw shows the time to spare (src/quality.ts, #3428)
function tick(now: number) { quality.frame(now); if (current?.held || !frames.due(now, current?.path === "/film/")) quality.idle(); else { const t0 = performance.now(); current?.frame?.(now); quality.work(performance.now() - t0); } requestAnimationFrame(tick); }
requestAnimationFrame(tick);
addEventListener("resize", () => current?.resize?.());
// the pixel budget changed (frames too slow, or fast enough to look sharper): every renderer of the view sizes itself again
quality.onChange(() => current?.resize?.());
perfOverlay();

/* ---- links between views move inside the page; everything else is a normal link ---- */
document.addEventListener("click", e => {
  const a = (e.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
  if (!a || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || a.target || a.origin !== location.origin) return;
  if (!ROUTES[normalise(a.pathname)]) return;
  e.preventDefault();
  if (normalise(a.pathname) !== current?.path || a.search !== location.search) void go(a.pathname + a.search, true);
});
addEventListener("popstate", () => { void go(location.pathname + location.search, false); });

// test hooks (e2e): which view is mounted, and the shared renderer
Object.assign(window, { __shell: { go: (p: string) => go(p, true), reopen: () => current && go(current.path, false), prefetched: () => [...prefetchDone], path: () => current?.path, stage: () => stageP, quality, frames, light, audio, radio, scene, deploys, progress, labFetched: () => labFetched } });

void go(location.pathname + location.search, false);
